(() => {
  const STORAGE_KEY = 'lcg-wishlist-v1';
  const TOGGLE_SELECTOR = '[data-lcg-wishlist-toggle]';
  let lastFocusedElement = null;
  let cachedItems = [];
  let storageError = null;

  const normalizeItems = (items) =>
    items
      .filter((item) => item && typeof item === 'object' && String(item.id || '').trim())
      .map((item) => {
        const currentPrice = String(item.currentPrice || item.price || '');
        const compareAtPrice = String(item.compareAtPrice || '');
        const onSale = (item.onSale === true || item.onSale === 'true')
          && Boolean(currentPrice)
          && Boolean(compareAtPrice)
          && currentPrice !== compareAtPrice;
        return {
          id: String(item.id),
          handle: String(item.handle || ''),
          title: String(item.title || ''),
          url: String(item.url || ''),
          image: String(item.image || ''),
          price: currentPrice,
          currentPrice,
          compareAtPrice,
          onSale,
          variantId: String(item.variantId || ''),
          directAdd: item.directAdd === true,
        };
      });

  const readItems = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) throw new Error('Invalid wishlist storage');
      cachedItems = normalizeItems(stored);
      if (storageError?.operation !== 'write') storageError = null;
    } catch (error) {
      if (storageError?.operation !== 'write') {
        storageError = {
          operation: 'read',
          message: window.CabaneI18n.pick("Impossible d’accéder aux favoris enregistrés sur cet appareil. Vérifie les réglages de confidentialité du navigateur, puis réessaie.","Unable to access the wishlist saved on this device. Check your browser’s privacy settings and try again."),
        };
      }
    }
    return [...cachedItems];
  };

  const writeItems = (items) => {
    const nextItems = normalizeItems(items);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextItems));
    } catch (error) {
      storageError = {
        operation: 'write',
        pendingItems: nextItems,
        message: window.CabaneI18n.pick("La modification n’a pas été enregistrée sur cet appareil. Vérifie que le stockage du navigateur est autorisé, puis réessaie.","Your change was not saved on this device. Check that browser storage is allowed and try again."),
      };
      return false;
    }
    cachedItems = nextItems;
    storageError = null;
    document.dispatchEvent(new CustomEvent('lcg:wishlist-change', { detail: { items: nextItems } }));
    return true;
  };

  const itemFromButton = (button) => {
    const currentPrice = String(button.dataset.productCurrentPrice || button.dataset.productPrice || '');
    const compareAtPrice = String(button.dataset.productCompareAtPrice || '');
    const onSale = button.dataset.productOnSale === 'true'
      && Boolean(currentPrice)
      && Boolean(compareAtPrice)
      && currentPrice !== compareAtPrice;
    return {
      id: String(button.dataset.productId || ''),
      handle: String(button.dataset.productHandle || ''),
      title: String(button.dataset.productTitle || ''),
      url: String(button.dataset.productUrl || ''),
      image: String(button.dataset.productImage || ''),
      price: currentPrice,
      currentPrice,
      compareAtPrice,
      onSale,
      variantId: String(button.dataset.productVariantId || ''),
      directAdd: button.dataset.productDirectAdd === 'true',
    };
  };

  const setText = (element, value) => {
    if (element && element.textContent !== String(value)) element.textContent = String(value);
  };

  const syncUtilityCounts = (items = readItems()) => {
    if (storageError?.operation !== 'read') {
      document.querySelectorAll('[data-lcg-wishlist-inline-count]').forEach((count) => setText(count, items.length));
    }

    const cartLink = document.querySelector('#cart-icon-bubble');
    const cartSource = document.querySelector('#CartDrawer [data-lcg-cart-inline-count]');
    const bubbleSource = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    const parseCartCount = (value) => {
      const count = Number.parseInt(String(value ?? '').trim(), 10);
      return Number.isFinite(count) && count >= 0 ? count : null;
    };
    // The refreshed header is shared by the cart page and drawer; an absent badge means an empty cart.
    const numericCartCount = cartLink
      ? (parseCartCount(bubbleSource?.textContent) ?? 0)
      : parseCartCount(cartSource?.textContent);
    if (numericCartCount !== null) {
      document.querySelectorAll('[data-lcg-cart-inline-count]').forEach((count) => setText(count, numericCartCount));
      if (cartLink) {
        cartLink.dataset.lcgCartCount = String(numericCartCount);
        cartLink.setAttribute('aria-label', `${window.CabaneI18n.pick("Panier, ","Cart, ")}${numericCartCount} ${numericCartCount > 1 ? window.CabaneI18n.pick("articles","items") : window.CabaneI18n.pick("article","item")}`);
      }
    }
  };

  const syncButton = (button, savedIds) => {
    if (!(button instanceof HTMLElement)) return;
    const isSaved = savedIds.has(String(button.dataset.productId || ''));
    const title = button.dataset.productTitle || window.CabaneI18n.pick("ce produit","this product");
    button.setAttribute('aria-pressed', String(isSaved));
    button.setAttribute('aria-label', `${isSaved ? window.CabaneI18n.pick("Retirer","Remove") : window.CabaneI18n.pick("Ajouter","Add")} ${title} ${isSaved ? 'des' : 'aux'}${window.CabaneI18n.pick(" favoris"," wishlist")}`);
    const visibleLabel = button.querySelector('[data-lcg-wishlist-label]');
    if (visibleLabel) visibleLabel.textContent = isSaved ? window.CabaneI18n.pick("Dans les favoris","In your wishlist") : window.CabaneI18n.pick("Ajouter aux favoris","Add to wishlist");
  };

  const syncButtons = (scope = document) => {
    const items = readItems();
    if (storageError?.operation !== 'read') {
      const savedIds = new Set(items.map((item) => item.id));
      scope.querySelectorAll?.(TOGGLE_SELECTOR).forEach((button) => syncButton(button, savedIds));
    }
    syncUtilityCounts(items);
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  };

  const setWishlistError = (message = '', { retryable = false } = {}) => {
    const error = document.querySelector('[data-lcg-wishlist-error]');
    if (!error) return;
    const messageNode = error.querySelector('[data-lcg-wishlist-error-message]');
    const retry = error.querySelector('[data-lcg-wishlist-retry]');
    if (messageNode) messageNode.textContent = message;
    if (retry) retry.hidden = !retryable;
    error.hidden = !message;
  };

  const showStorageError = () => {
    if (!storageError) return false;
    setWishlistError(storageError.message, { retryable: true });
    return true;
  };

  const renderDrawer = () => {
    const drawer = document.querySelector('[data-lcg-wishlist-drawer]');
    if (!drawer) return;
    const itemsRoot = drawer.querySelector('[data-lcg-wishlist-items]');
    const empty = drawer.querySelector('[data-lcg-wishlist-empty]');
    const count = drawer.querySelector('[data-lcg-wishlist-drawer-count]');
    if (!itemsRoot || !empty || !count) return;

    const items = readItems();
    const storageUnavailable = Boolean(storageError);
    count.textContent = storageUnavailable && items.length === 0
      ? window.CabaneI18n.pick("Favoris indisponibles","Wishlist unavailable")
      : window.CabaneI18n.pick(`${items.length} produit${items.length > 1 ? 's' : ''} gardé${items.length > 1 ? 's' : ''} de côté`, `${items.length} saved product${items.length === 1 ? '' : 's'}`);
    empty.hidden = storageUnavailable || items.length > 0;
    itemsRoot.replaceChildren();

    items.forEach((item) => {
      const article = createElement(window.CabaneI18n.pick("article","item"), 'lcg-wishlist__item');
      const imageLink = createElement('a', 'lcg-wishlist__item-image');
      imageLink.href = item.url || '#';
      imageLink.setAttribute('aria-label', item.title || window.CabaneI18n.pick("Voir le produit","View product"));
      if (item.image) {
        const image = createElement('img');
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 240;
        image.height = 240;
        imageLink.append(image);
      }

      const copy = createElement('div', 'lcg-wishlist__item-copy');
      const title = createElement('a', 'lcg-wishlist__item-title', item.title || window.CabaneI18n.pick("Produit","product"));
      title.href = item.url || '#';
      const actions = createElement('div', 'lcg-wishlist__item-actions');
      const priceNodes = [];
      if (item.onSale && item.compareAtPrice && item.currentPrice) {
        const compareAtPrice = createElement('s', 'lcg-wishlist__item-price', item.compareAtPrice);
        compareAtPrice.setAttribute('aria-label', `${window.CabaneI18n.pick("Prix initial ","Original price ")}${item.compareAtPrice}`);
        const currentPrice = createElement('strong', 'lcg-wishlist__item-price lcg-wishlist__item-price--sale', item.currentPrice);
        currentPrice.setAttribute('aria-label', `${window.CabaneI18n.pick("Prix promotionnel ","Sale price ")}${item.currentPrice}`);
        priceNodes.push(compareAtPrice, currentPrice);
      } else if (item.currentPrice) {
        const currentPrice = createElement('span', 'lcg-wishlist__item-price', item.currentPrice);
        currentPrice.setAttribute('aria-label', `${window.CabaneI18n.pick("Prix ","Price ")}${item.currentPrice}`);
        priceNodes.push(currentPrice);
      }

      if (item.directAdd && item.variantId) {
        const add = createElement('button', 'lcg-wishlist__item-primary', window.CabaneI18n.pick("Ajouter au panier","Add to cart"));
        add.type = 'button';
        add.dataset.lcgWishlistAdd = item.variantId;
        actions.append(add);
      } else {
        const choose = createElement('a', 'lcg-wishlist__item-primary', window.CabaneI18n.pick("Choisir les options","Choose options"));
        choose.href = item.url || '#';
        actions.append(choose);
      }

      const remove = createElement('button', 'lcg-wishlist__item-remove', window.CabaneI18n.pick("Retirer","Remove"));
      remove.type = 'button';
      remove.dataset.lcgWishlistRemove = item.id;
      remove.setAttribute('aria-label', `${window.CabaneI18n.pick("Retirer ","Remove ")}${item.title || window.CabaneI18n.pick("ce produit","this product")}${window.CabaneI18n.pick(" des favoris"," from wishlist")}`);
      actions.append(remove);
      copy.append(title, ...priceNodes, actions);
      article.append(imageLink, copy);
      itemsRoot.append(article);
    });

    syncUtilityCounts(items);
    if (!showStorageError()) setWishlistError();
  };

  const openDrawer = () => {
    const drawer = document.querySelector('[data-lcg-wishlist-drawer]');
    if (!drawer) return;
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.classList.contains('active')) cartDrawer.close();

    lastFocusedElement = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    renderDrawer();
    document.body.classList.add('lcg-wishlist-locked');
    window.requestAnimationFrame(() => {
      const panel = drawer.querySelector('.lcg-wishlist__panel');
      if (typeof window.trapFocus === 'function' && panel) window.trapFocus(panel, panel);
      else panel?.focus();
    });
  };

  const closeDrawer = ({ restoreFocus = true } = {}) => {
    const drawer = document.querySelector('[data-lcg-wishlist-drawer]');
    if (!drawer || drawer.getAttribute('aria-hidden') === 'true') return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lcg-wishlist-locked');
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus();
    if (restoreFocus && lastFocusedElement instanceof HTMLElement && lastFocusedElement.isConnected) lastFocusedElement.focus();
  };

  const openCart = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    closeDrawer({ restoreFocus: false });
    window.requestAnimationFrame(() => cartDrawer.open(document.querySelector('#cart-icon-bubble')));
  };

  const addWishlistItemToCart = async (button) => {
    const variantId = button.dataset.lcgWishlistAdd;
    if (!variantId || button.disabled) return;
    button.disabled = true;
    if (!storageError) setWishlistError();
    button.textContent = window.CabaneI18n.pick("Ajout…","Adding…");
    button.setAttribute('aria-label', window.CabaneI18n.pick("Ajout au panier en cours","Adding to cart"));

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (typeof cartDrawer?.getSectionsToRender === 'function') {
        formData.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const cart = await response.json().catch(() => ({}));
      if (!response.ok || cart.status) {
        throw new Error(cart.description || cart.message || window.CabaneI18n.pick("Impossible d’ajouter ce produit au panier.","Unable to add this product to your cart."));
      }
      closeDrawer({ restoreFocus: false });
      if (typeof cartDrawer?.renderContents === 'function') cartDrawer.renderContents(cart);
      else window.location.assign(window.Shopify.routes.root + 'cart');
    } catch (error) {
      button.disabled = false;
      button.textContent = window.CabaneI18n.pick("Réessayer","Try again");
      button.setAttribute('aria-label', window.CabaneI18n.pick("Réessayer l’ajout au panier","Try adding to cart again"));
      setWishlistError(`${error.message || window.CabaneI18n.pick("Impossible d’ajouter ce produit au panier.","Unable to add this product to your cart.")} Réessayer.`);
    }
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest(TOGGLE_SELECTOR);
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      const product = itemFromButton(toggle);
      if (!product.id) return;
      const items = readItems();
      if (storageError?.operation === 'read') {
        openDrawer();
        return;
      }
      const existingIndex = items.findIndex((item) => item.id === product.id);
      if (existingIndex >= 0) items.splice(existingIndex, 1);
      else items.unshift(product);
      if (!writeItems(items)) openDrawer();
      return;
    }

    const remove = event.target.closest('[data-lcg-wishlist-remove]');
    if (remove) {
      const id = String(remove.dataset.lcgWishlistRemove || '');
      const items = readItems();
      if (storageError?.operation === 'read') {
        openDrawer();
        return;
      }
      if (!writeItems(items.filter((item) => item.id !== id))) renderDrawer();
      return;
    }

    const retry = event.target.closest('[data-lcg-wishlist-retry]');
    if (retry) {
      if (storageError?.operation === 'write' && Array.isArray(storageError.pendingItems)) {
        const pendingItems = storageError.pendingItems;
        if (!writeItems(pendingItems)) renderDrawer();
      } else {
        storageError = null;
        readItems();
        syncButtons();
        renderDrawer();
      }
      return;
    }

    const add = event.target.closest('[data-lcg-wishlist-add]');
    if (add) {
      addWishlistItemToCart(add);
      return;
    }

    if (event.target.closest('[data-lcg-open-wishlist]')) {
      document.dispatchEvent(new CustomEvent('lcg:wishlist-open'));
      return;
    }
    if (event.target.closest('[data-lcg-open-cart]')) {
      openCart();
      return;
    }
    if (event.target.closest('[data-lcg-wishlist-close]')) closeDrawer();
  });

  document.addEventListener('lcg:wishlist-open', openDrawer);
  document.addEventListener('lcg:wishlist-change', () => {
    syncButtons();
    renderDrawer();
  });
  document.addEventListener('shopify:section:load', (event) => syncButtons(event.target));
  document.addEventListener('focusin', (event) => {
    const button = event.target.closest?.(TOGGLE_SELECTOR);
    if (!button) return;
    const items = readItems();
    if (storageError?.operation !== 'read') syncButton(button, new Set(items.map((item) => item.id)));
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    storageError = null;
    syncButtons();
    renderDrawer();
  });

  const observeRoot = document.querySelector('#MainContent');
  if (observeRoot) {
    const observer = new MutationObserver((records) => {
      const hasWishlistButtons = records.some((record) =>
        [...record.addedNodes].some((node) => node instanceof Element && (node.matches?.(TOGGLE_SELECTOR) || node.querySelector?.(TOGGLE_SELECTOR)))
      );
      if (hasWishlistButtons) syncButtons(observeRoot);
    });
    observer.observe(observeRoot, { childList: true, subtree: true });
  }

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    new MutationObserver(() => syncUtilityCounts()).observe(cartDrawer, { childList: true, subtree: true });
  }
  const cartLink = document.querySelector('#cart-icon-bubble');
  if (cartLink) {
    new MutationObserver(() => syncUtilityCounts()).observe(cartLink, { childList: true, subtree: true });
  }

  syncButtons();
  renderDrawer();
})();
